import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { scrollToSection } from "../lib/scroll";

const HERO_PHOTO =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_voice-extended/artifacts/uc35fpzi_IMG_6902.webp";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const textY = useTransform(scrollY, [0, 900], [0, -60]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative flex items-center overflow-hidden pt-24 md:pt-16 md:min-h-screen"
    >
      <div className="mx-auto w-full max-w-[1600px] px-6 md:px-12 grid grid-cols-12 gap-8 items-center">
        <motion.div style={{ y: textY, opacity: fade }} className="col-span-12 lg:col-span-8 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xs uppercase tracking-[0.3em] text-foreground/50 mb-12 md:mb-20"
            data-testid="hero-kicker"
          >
            {t.hero.kicker}
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-serif font-normal text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-foreground"
          >
            <MaskedLine delay={0.35}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.5}>{t.hero.line2}</MaskedLine>
            <MaskedLine delay={0.65}>
              <em className="not-italic border-b border-foreground/30">{t.hero.line3}</em>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14 md:mt-20 max-w-lg text-base md:text-lg leading-relaxed text-foreground/70 tracking-wide"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollToSection("contact")}
              className="group inline-flex items-center gap-4 text-xs uppercase tracking-[0.25em] border border-foreground rounded-full px-8 py-4 hover:bg-foreground hover:text-background transition-colors duration-500"
            >
              {t.hero.cta}
              <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
            </button>
          </motion.div>
        </motion.div>

        <div className="hidden lg:block col-span-4 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <figure data-testid="hero-photo">
              <div className="overflow-hidden">
                <img
                  src={HERO_PHOTO}
                  alt={t.altHero}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
              <figcaption className="mt-4 text-[11px] tracking-[0.12em] text-foreground/45">
                {t.capHero}
              </figcaption>
            </figure>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
